import { useMemo, useState, useEffect, useId } from 'react'
import {
  AbsoluteFill,
  continueRender,
  delayRender,
  useCurrentFrame,
  useVideoConfig,
} from 'remotion'
import getFontData from '@/lib/get-font-data'
import { subtitleTextTransformToCss } from '@/lib/subtitles/subtitleTextTransform'
import { buildSubtitleTextShadow, type SubtitleShadowStyle } from '@/lib/subtitles/subtitleOutline'
import { AVAILABLE_ANIMATIONS } from '@/constants/animations'
import SubtitleWord from './SubtitleWord'
import convertTimeStringToSeconds from '@/lib/convert-time-string-to-seconds'
import reorganizeWords from '@/lib/reorganize-words'
import { scaleDesignPixels } from '@/lib/render/designScale'
import {
  circuloArcPath,
  circuloArcPathLength,
  fitCirculoWordFontSize,
} from '@/lib/layouts/circuloGeometry'
import type { CirculoPresetId } from '@/lib/layouts/types'

// Type definitions
interface Word {
  word: string
  startTime: string
  endTime: string
  start: number
  end: number
  startFrame: number
  endFrame: number
  wordIndex: number
  isCurrentWord?: boolean
}

interface Segment {
  startTime: string
  endTime: string
  words: Word[]
  start: number
  end: number
  startFrame: number
  endFrame: number
}

interface Transcription {
  segments: Segment[]
}

export interface SubtitleLineStyle {
  alignItems?: string
  justifyContent?: string
  textTransform?: string
  fontSize?: number
  fontColor?: string
  fontWeight?: number
  gap?: number
  lineHeight?: number
  fontName?: string
  fontUrl?: string
  enableBackground?: boolean
  paddingY?: number
  paddingX?: number
  backgroundWidthPct?: number
  backgroundHeightPct?: number
  compWidth?: number
  compHeight?: number
  backgroundColor?: string
  borderRadius?: number
  horizontalMarginPercentage?: number
  horizontalMargin?: number
  verticalMarginPercentage?: number
  verticalMargin?: number
  highlightCurrentWord?: boolean
  shadowSize?: number
  shadowColor?: string
  /** Outline + drop shadow — see src/lib/subtitles/subtitleOutline.ts. */
  strokeColor?: string
  strokeWidth?: number
  shadowStyle?: SubtitleShadowStyle
  shadowOffsetX?: number
  shadowOffsetY?: number
}

export interface SubtitleHighlightStyle {
  fontColor?: string
}

export interface SubtitleHighlightBoxStyle {
  enabled?: boolean
  paddingY?: number
  paddingX?: number
  backgroundColor?: string
  borderRadius?: number
}

export interface ReorganizeOptions {
  maxWordsPerLine: number
  maxSecondsPerLine: number
  maxCharactersPerLine: number
  cutPhrases: boolean
  removePunctuation: boolean
}

export interface Subtitle {
  transcription: Transcription
  animation?: string
  lineStyle?: SubtitleLineStyle
  highlightStyle?: SubtitleHighlightStyle
  highlightBoxStyle?: SubtitleHighlightBoxStyle
  reorganizeOptions?: ReorganizeOptions
}

interface Animation {
  name: string
  lineStyle?: (baseStyle: any, line: any, frame: number, fps: number) => any
  highlightStyle?: (baseStyle: any, word: any, frame: number, fps: number) => any
  highlightStyleBefore?: (baseStyle: any) => any
  highlightStyleAfter?: (baseStyle: any) => any
}

interface SubtitleProps {
  subtitle: Subtitle
  /**
   * Clip preview/export fontSize is authored for a 1080p frame. Scale it
   * to the live composition so 720p exports stay WYSIWYG. Brand-kit
   * SubtitlePreview already picks px for its own 1280×720 canvas — pass
   * false there.
   */
  scaleDesignPixels?: boolean
  curve?: { position: CirculoPresetId }
}

const wait = (ms: number) => new Promise((r) => { setTimeout(r, ms) })

const compatibleBorderRadius = (borderRadius: number): number => {
  if (borderRadius >= 1) {
    // New Pattern
    return borderRadius / 100
  }

  // Old Pattern
  return borderRadius
}

const backOff = (operation: () => Promise<any>, delay: number, retries: number): Promise<any> => new Promise((resolve, reject) => {
  operation()
    .then(resolve)
    .catch((reason) => {
      if (retries > 0) {
        return wait(delay)
          .then(backOff.bind(null, operation, delay, retries - 1))
          .then(resolve)
          .catch(reject)
      }
      return reject(reason)
    })
})

const makeShadow = (style: SubtitleLineStyle): string | null => {
  if (!style) {
    return null
  }

  return buildSubtitleTextShadow({
    fontSizePx: style.fontSize || 0,
    strokeColor: style.strokeColor,
    strokeWidth: style.strokeWidth,
    shadowStyle: style.shadowStyle,
    shadowColor: style.shadowColor,
    shadowSize: style.shadowSize,
    shadowOffsetX: style.shadowOffsetX,
    shadowOffsetY: style.shadowOffsetY,
  })
}

const getHorizontalMargin = (style: SubtitleLineStyle, width: number): string => {
  if (typeof style?.horizontalMarginPercentage !== 'undefined') {
    const margin = ((style.horizontalMarginPercentage * width) / 100) / 2
    return `0px ${margin || 0}px`
  }

  return `0px ${style?.horizontalMargin || 0}px`
}

const transformVerticalMarginAlignment = (margin: number, style: SubtitleLineStyle): number => {
  if (style.alignItems === 'center') {
    return (margin - 50)
  }

  if (style.alignItems === 'flex-end') {
    return -margin
  }

  return margin
}

const getVerticalMargin = (style: SubtitleLineStyle, height: number): string => {
  if (typeof style?.verticalMarginPercentage !== 'undefined') {
    const transformedMargin = transformVerticalMarginAlignment(
      style.verticalMarginPercentage,
      style,
    )
    const margin = (transformedMargin * height) / 100

    return `translateY(${margin}px)`
  }

  return `translateY(${style?.verticalMargin || 0}px)`
}

const makeStyle = (style: SubtitleLineStyle, height: number): React.CSSProperties => ({
  alignItems: style?.alignItems,
  justifyContent: style?.justifyContent,
  ...subtitleTextTransformToCss(style?.textTransform),
  fontSize: `${style?.fontSize || 0}px`,
  transform: getVerticalMargin(style, height),
  textShadow: makeShadow(style),
  color: style?.fontColor,
  gap: `${style?.gap}em`,
  lineHeight: `${style?.lineHeight || 1}em`,
  fontFamily: style?.fontName || 'Komika Title Axis',
  background: 'gray',
  padding: style?.enableBackground ? `${style.paddingY}em ${style.paddingX}em` : 0,
  // backgroundWidthPct/HeightPct (100 = hug the text, the floor) grow the box
  // via BORDERS in the background color: borders paint outward like the
  // background, add no inner padding around the text, and escape the
  // horizontal-margin container — so 200% reaches the video edges as a faixa.
  ...(style?.enableBackground
    ? (() => {
        const extraX = (Math.max(100, style.backgroundWidthPct ?? 100) - 100) * 0.005 * (style.compWidth ?? 0);
        const extraY = (Math.max(100, style.backgroundHeightPct ?? 100) - 100) * 0.005 * (style.compHeight ?? 0);
        return extraX > 0 || extraY > 0
          ? {
              borderLeft: `${extraX}px solid ${style?.backgroundColor}`,
              borderRight: `${extraX}px solid ${style?.backgroundColor}`,
              borderTop: `${extraY}px solid ${style?.backgroundColor}`,
              borderBottom: `${extraY}px solid ${style?.backgroundColor}`,
            }
          : {};
      })()
    : {}),
  backgroundColor: style?.enableBackground ? style?.backgroundColor : 'transparent',
  borderRadius: style?.enableBackground ? `${compatibleBorderRadius(style?.borderRadius || 0)}em` : 0,
})

const makeHighlightStyle = (style: SubtitleHighlightStyle): React.CSSProperties => ({
  color: style?.fontColor,
})

const makeHighlightBoxStyle = (style: SubtitleHighlightBoxStyle): React.CSSProperties => ({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  position: 'absolute',
  inset: `${-style.paddingY}em ${-style.paddingX}em`,
  backgroundColor: style?.backgroundColor,
  borderRadius: `${compatibleBorderRadius(style?.borderRadius || 0)}em`,
  zIndex: 99,
})

const makeAllStyles = (
  subtitle: Subtitle,
  width: number,
  height: number,
  shouldScaleDesignPixels: boolean,
  curveActive: boolean,
) => {
  const { lineStyle: rawLineStyle = {}, highlightStyle = {}, highlightBoxStyle = {} } = subtitle
  // Composition dims feed the faixa (banner) growth of the background box —
  // sourced here so BOTH the editor preview and the headless render get them
  // without every caller having to thread them through.
  const lineStyle = { ...rawLineStyle, compWidth: width, compHeight: height }
  const sized = shouldScaleDesignPixels
    ? {
        ...lineStyle,
        fontSize: scaleDesignPixels(lineStyle.fontSize || 0, width, height),
      }
    : lineStyle

  return {
    rootStyle: {
      padding: getHorizontalMargin(lineStyle, width),
    },
    lineStyle: makeStyle(sized, height),
    highlightStyle: lineStyle?.highlightCurrentWord ? makeHighlightStyle(highlightStyle) : null,
    highlightBoxStyle: (
      !curveActive
      && lineStyle?.highlightCurrentWord
      && highlightBoxStyle?.enabled
        ? makeHighlightBoxStyle(highlightBoxStyle) : null
    ),
  }
}

const DEFAULT_HIGHLIGHT_ANIMATION = (baseStyle: Record<string, unknown> | null) => baseStyle || {}

function curveShadowStroke(
  style: SubtitleLineStyle | undefined,
  fontPx: number,
): { color: string; width: number } | null {
  // An explicit outline wins. SVG strokes are centered on the glyph edge and
  // the fill paints over the inner half, so double it to match the HTML ring.
  if (style?.strokeColor && style.strokeWidth) {
    return { color: style.strokeColor, width: 2 * (style.strokeWidth / 100) * fontPx }
  }
  if (!style?.shadowColor || !style.shadowSize) return null
  const width = Math.max(2, Math.round((style.shadowSize / 100) * fontPx * 0.35))
  return { color: style.shadowColor, width }
}

const useLoadedFont = (fontId: string, fontUrl?: string) => {
  const [waitForFont] = useState(() => delayRender())

  useEffect(() => {
    const loadFontIfNotLoaded = async () => {
      // If a direct font URL is provided (e.g. from server-side render data), use it directly
      if (fontUrl) {
        const font = new FontFace(fontId, `url(${fontUrl})`)
        await font.load()
        document.fonts.add(font)
        continueRender(waitForFont)
        return
      }

      // Otherwise look up font data by ID
      const fontData = await getFontData(fontId)
      if (!fontData) {
        continueRender(waitForFont)
        return
      }
      const font = new FontFace(fontData.id, `url(${fontData.url})`)
      await font.load()
      document.fonts.add(font)
      continueRender(waitForFont)
    }
    backOff(() => loadFontIfNotLoaded(), 1000, 10).catch(() => {
      continueRender(waitForFont)
    })
  }, [fontId, fontUrl])
}

export default function Subtitle({ subtitle, scaleDesignPixels: shouldScaleDesignPixels = true, curve }: SubtitleProps) {
  const [line, setLine] = useState<Segment | null>(null)
  const { fps, width, height } = useVideoConfig()
  const currentFrame = useCurrentFrame()
  const arcDomId = `circulo-arc-${useId().replace(/:/g, '')}`

  const transcription = useMemo(() => {
    let segments: any[] = subtitle.transcription.segments;

    // Apply word reorganization if options are provided. Curve mode is one
    // transcript word on the arc, so regrouping would hide the spoken unit.
    if (!curve && subtitle.reorganizeOptions) {
      const project = { subtitle: { transcription: subtitle.transcription } };
      segments = reorganizeWords(project, subtitle.reorganizeOptions);
    }

    return segments.map((line): Segment => {
      const start = convertTimeStringToSeconds(line.startTime)
      const end = convertTimeStringToSeconds(line.endTime)
      return {
        ...line,
        start,
        end,
        startFrame: Math.round(start * fps),
        endFrame: Math.round(end * fps),
        words: line.words.map((word, wordIndex) => {
          const start = convertTimeStringToSeconds(word.startTime)
          const end = convertTimeStringToSeconds(word.endTime)
          return {
            ...word,
            start,
            end,
            startFrame: Math.round(start * fps),
            endFrame: Math.round(end * fps),
            wordIndex,
          }
        }),
      }
    })
  }, [subtitle, fps, curve])

  const {
    rootStyle,
    lineStyle: baseLineStyle,
    highlightStyle,
    highlightBoxStyle,
  } = useMemo(() => (
    makeAllStyles(subtitle, width, height, shouldScaleDesignPixels, Boolean(curve))
  ), [subtitle, width, height, shouldScaleDesignPixels, curve])
  const { transform, ...lineStyle } = baseLineStyle

  useLoadedFont(lineStyle.fontFamily || 'Komika Title Axis', subtitle.lineStyle?.fontUrl)

  const animation = useMemo((): Animation => (
    AVAILABLE_ANIMATIONS.find((a: Animation) => a.name === subtitle.animation) || ({} as Animation)
  ), [subtitle])

  const lineElement = useMemo(() => {
    if (curve) return null
    return line?.words?.map((word) => (
      <SubtitleWord
        key={word.wordIndex}
        animation={animation}
        word={word}
        highlightStyle={highlightStyle}
        highlightBoxStyle={highlightBoxStyle}
      />
    ))
  }, [
    curve,
    line,
    animation,
    highlightStyle,
    highlightBoxStyle,
  ])

  const animationStyle = useMemo(() => {
    if (!line) {
      return {}
    }

    if (animation && typeof animation === 'object' && 'lineStyle' in animation && typeof animation.lineStyle === 'function') {
      return animation.lineStyle({}, line, currentFrame, fps)
    }

    return {}
  }, [line, animation, currentFrame, fps])

  useEffect(() => {
    const line = transcription.find((l) => {
      return currentFrame >= l.startFrame && currentFrame <= l.endFrame
    })

    if (!line) {
      setLine(null)
      return
    }

    setLine({
      ...line,
      words: line.words.map((word) => ({
        ...word,
        isCurrentWord: word.startFrame <= currentFrame && word.endFrame >= currentFrame,
      })),
    })
  }, [currentFrame, transcription])

  if (curve) {
    const word = line?.words?.find((w) => w.isCurrentWord)
    if (!word) {
      return null
    }

    const highlightAnimation = (
      typeof animation.highlightStyle === 'function'
        ? animation.highlightStyle
        : DEFAULT_HIGHLIGHT_ANIMATION
    )
    const currentWordHighlightStyle = highlightAnimation(
      highlightStyle as Record<string, unknown> | null,
      word,
      currentFrame,
      fps,
    ) as React.CSSProperties
    const {
      transform: highlightTransform,
      transformOrigin,
      color: highlightColor,
      fill: highlightFill,
      fontSize: highlightFontSize,
      ...highlightRest
    } = currentWordHighlightStyle
    const authoredSize = subtitle.lineStyle?.fontSize || 50
    const maxSize = shouldScaleDesignPixels
      ? scaleDesignPixels(authoredSize, width, height)
      : authoredSize
    const fittedSize = fitCirculoWordFontSize({
      text: word.word,
      fontFamily: String(lineStyle.fontFamily || 'sans-serif'),
      fontWeight: subtitle.lineStyle?.fontWeight || 800,
      maxSize,
      minSize: 18,
      pathLength: circuloArcPathLength(curve.position, width, height),
    })
    const glow = curveShadowStroke(subtitle.lineStyle, Number(fittedSize) || authoredSize)
    const scaleHost = Boolean(highlightTransform && highlightTransform !== 'none')
    const fillColor = highlightColor || highlightFill || lineStyle.color
    const wordStyle: React.CSSProperties = {
      fontFamily: lineStyle.fontFamily,
      fontWeight: subtitle.lineStyle?.fontWeight || 800,
      ...subtitleTextTransformToCss(subtitle.lineStyle?.textTransform),
      fontSize: highlightFontSize || fittedSize,
      ...highlightRest,
      // `currentColor` in animation filters (Glow) means the word's color.
      color: fillColor,
    }
    const wordOnArc = (key: string) => (
      <textPath
        key={key}
        href={`#${arcDomId}`}
        xlinkHref={`#${arcDomId}`}
        startOffset="50%"
        method="align"
      >
        {word.word}
      </textPath>
    )

    return (
      <AbsoluteFill style={{ pointerEvents: 'none' }}>
        <svg
          width={width}
          height={height}
          style={{ position: 'absolute', inset: 0, overflow: 'visible', fontSize: fittedSize }}
        >
          <path id={arcDomId} d={circuloArcPath(curve.position, width, height)} fill="none" stroke="none" />
          <g
            data-testid="circulo-word-motion"
            style={scaleHost ? {
              transform: highlightTransform,
              transformOrigin: transformOrigin || 'center center',
              transformBox: 'fill-box',
            } : undefined}
          >
            {glow && (
              <text
                data-testid="circulo-word-glow"
                fill="none"
                stroke={glow.color}
                strokeWidth={glow.width}
                strokeLinejoin="round"
                textAnchor="middle"
                style={{ ...wordStyle, fill: 'none' }}
              >
                {wordOnArc('glow')}
              </text>
            )}
            <text
              data-testid="circulo-word"
              textAnchor="middle"
              style={{ ...wordStyle, fill: fillColor }}
            >
              {wordOnArc('fill')}
            </text>
          </g>
        </svg>
      </AbsoluteFill>
    )
  }

  if (!lineElement) {
    return null
  }

  return (
    <AbsoluteFill
      className="subtitle-preview"
      style={{
        display: 'flex',
        flexDirection: 'row',
        flexWrap: 'wrap',
        justifyContent: 'center',
        textAlign: 'center',
        fontSize: `${shouldScaleDesignPixels ? scaleDesignPixels(28, width, height) : 28}px`,
        textShadow: `0px 0px ${shouldScaleDesignPixels ? scaleDesignPixels(8, width, height) : 8}px black`,
        alignItems: lineStyle.alignItems || 'center',
        pointerEvents: 'none',
        ...rootStyle,
      }}
    >
      <div
        style={{
          transform,
          display: 'inline-flex',
          flexWrap: 'wrap',
          width: '100%',
        }}
      >
        <div
          style={{
            display: 'inline-flex',
            flexWrap: 'wrap',
            width: '100%',
            ...lineStyle,
            ...animationStyle,
          }}
        >
          { lineElement }
        </div>
      </div>
    </AbsoluteFill>
  )
}
